import ApiClient from 'api-client/ApiClient'
import store from 'app/store'
import ActionsSet from 'core/actions/ActionsSet'
import CreateAction from 'core/actions/CreateAction'
import CustomAction from 'core/actions/CustomAction'
import DeleteAction from 'core/actions/DeleteAction'
import ListAction from 'core/actions/ListAction'
import UpdateAction from 'core/actions/UpdateAction'
import { cacheActions } from 'core/caching/cacheReducers'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'

const { dispatch } = store
const { glance } = ApiClient.getInstance()

export const imageActions = ActionsSet.make<DataKeys.Images>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.Images,
  cacheKey: DataKeys.Images,
})

export const listImages = imageActions.add(
  new ListAction<DataKeys.Images>(async () => {
    return glance.getImages()
  }),
)

export const createImage = imageActions.add(
  new CreateAction<DataKeys.Images, { body }>(async ({ body }) => {
    const created = await glance.createImage(body)
    const imageId = created.id
    const newImage = await glance.getImage(imageId)
    return newImage
  }),
)

export const updateImage = imageActions.add(
  new UpdateAction<
    DataKeys.Images,
    {
      id: string
      body: unknown
    }
  >(async ({ id, body }) => {
    const updatedImage = await glance.patchImage({ id, body })
    return updatedImage
  }),
)

export const deleteImage = imageActions.add(
  new DeleteAction<DataKeys.Images, { id: string; deleteSnapshots?: boolean }>(
    async ({ id, deleteSnapshots = false }) => {
      await glance.deleteImage(id, deleteSnapshots)
    },
  ),
)

export const uploadImage = imageActions.add(
  new CustomAction<
    DataKeys.Images,
    {
      id: string
      body?: any
      onUploadProgress?: (progressEvent: any) => void
      abortController?: AbortController
      onError?: (error: any) => void
    }
  >(
    'uploadImage',
    async ({ id, body, onUploadProgress, abortController, onError }) => {
      try {
        const config = {
          onUploadProgress: (progressEvent) => {
            if (abortController?.signal.aborted) return
            // Update the progress state here
            onUploadProgress?.(progressEvent)
          },
          abortController,
        }
        await glance.uploadImage({ id, body }, config)
        const image = await glance.getImage(id)
        return image
      } catch (error) {
        abortController?.abort()
        onError?.(error)
        throw error
      }
    },
    (result, { id }) => {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.Images,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
    },
  ),
)

export const importImage = imageActions.add(
  new CustomAction<DataKeys.Images, { id: string; body?: any }>(
    'importImage',
    async ({ id, body }) => {
      try {
        await glance.importImage({ id, body })
        const image = await glance.getImage(id)
        return image
      } catch (error) {
        throw error
      }
    },
    (result, { id }) => {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.Images,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
    },
  ),
)

// Copy an already-uploaded image to every configured store (multi-store fan-out, FR-7).
// Business-level "copy to all stores" that runs on the admin/host endpoint via the generic
// Glance.importImageOnHost. It is intentionally NOT wired to Glance.importImage (web-download,
// public endpoint) — see Glance.ts for why the public and host import paths must stay separate.
export const copyImage = imageActions.add(
  new CustomAction<DataKeys.Images, { id: string; body?: any }>(
    'copyImage',
    async ({ id, body }) => {
      await glance.importImageOnHost({ id, body })
      const image = await glance.getImage(id)
      return image
    },
    (result, { id }) => {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.Images,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)

export const testHostGlance = async (endpoint: string) => {
  return glance.testHostGlanceAccess(endpoint)
}

export const getHostGlanceEndpoint = async () => {
  return glance.getHostGlanceEndpoint()
}

export const getImagesWithName = async (name) => {
  return glance.getImagesWithName(name)
}

// Re-fetches one image and writes the fresh copy back into the cache. Used to poll multi-store
// import progress: the Stores column re-derives from the cached image, so refreshing the cache is
// all the UI needs — no separate reload. (FR-5)
export const refreshImage = imageActions.add(
  new CustomAction<DataKeys.Images, { id: string }>(
    'refreshImage',
    async ({ id }) => {
      return glance.getImage(id)
    },
    (result, { id }) => {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.Images,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)
